import { FC, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { setToastMessage } from '@stores/App/slice';
import { CohortStatus, COHORT_STATUS_MAP, TOAST_TYPES } from '@constants/constants';
import { GenericTable } from '@components';
import { CommissionPayout } from '@api/types/commissions';
import { useLazyGetFinalCommissionsQuery } from '@api/commissions';

interface FinalCommissionProps {
  id: string | undefined;
}

const FinalCommission: FC<FinalCommissionProps> = ({ id }) => {
  const [finalCommission, setFinalCommission] = useState<CommissionPayout[]>([]);

  const dispatch = useDispatch();

  const [getFinalCommissions, { isLoading: isFetchingFinalCommissions }] =
    useLazyGetFinalCommissionsQuery();

  useEffect(() => {
    if (!id) return;

    fetchFinalCommissions();
  }, [id]);

  const fetchFinalCommissions = async () => {
    try {
      const response = await getFinalCommissions(id as string).unwrap();
      setFinalCommission(response.data);
    } catch (error: any) {
      console.error(error);
      dispatch(
        setToastMessage({
          message: error.message || 'Failed to fetch final commissions',
          type: TOAST_TYPES.ERROR,
        }),
      );
    }
  };

  const columns = [
    {
      key: 'cohortClosureMonth',
      label: 'Cohort Closure Month',
    },
    {
      key: 'dealName',
      label: 'Deal Name',
    },
    {
      key: 'fundingMonth',
      label: 'Funding Month',
    },
    {
      key: 'commission',
      label: 'Commission',
    },
    {
      key: 'cohortStatus',
      label: 'Status',
      render: (value: CohortStatus) => (
        <Typography fontSize="0.875rem" fontWeight={550}>
          {COHORT_STATUS_MAP[value]}
        </Typography>
      ),
    },
  ];

  const meta = {
    columns: [
      {
        id: 'cohortClosureMonth',
        name: 'Cohort Closure Month',
        type: 'string',
      },
      { id: 'dealName', name: 'Deal Name', type: 'string' },
      { id: 'fundingMonth', name: 'Funding Month', type: 'string' },
      {
        id: 'commission',
        name: 'Commission',
        type: 'currency',
        currencyFormat: { currency: 'dollar', scale: 2 },
      },
      { id: 'status', name: 'Status', type: 'string' },
    ],
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        padding: '1rem',
        backgroundColor: '#fff',
        borderRadius: '12px',
      }}
    >
      <Box>
        <Typography fontSize={'1rem'} fontWeight={700} color={'#162936'} lineHeight={'1.25rem'}>
          Final Commission Amount
        </Typography>
      </Box>

      <Box>
        <GenericTable<any>
          isLoading={isFetchingFinalCommissions}
          data={finalCommission}
          columns={columns}
          meta={meta}
          actions={[]}
        />
      </Box>
    </Box>
  );
};

export default FinalCommission;
